import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { isLedgerXml } from "../refunds/SmartRefundWorkspace";

type Item = {
  gtip: string; quantity: string; unit_code: string; invoice_no: string;
  invoice_date: string; invoice_series: string; buyer_name: string;
  transaction_code: string; line_amount: string;
};
type Declaration = {
  declaration_type?: "GÇB" | "ETGB" | "GGB"; vkn: string; registration_no: string; registration_date: string; closure_date: string;
  currency: string; invoice_amount: string; import_vat: string; issuing_authority: string; items: Item[];
};
type CustomsDocument = {
  id: number; kind: "ggb" | "gcb"; taxpayer: number; taxpayer_title: string;
  taxpayer_vkn: string; filename: string; confirmed: boolean; declaration: Declaration;
  warnings: string[]; cases: { id: number; title: string; transaction_code: string }[]; source_url: string;
};
type UploadResult = { filename: string; status: string; error?: string; document?: CustomsDocument };
const blankItem = (): Item => ({ gtip: "", quantity: "", unit_code: "", invoice_no: "", invoice_date: "",
  invoice_series: "", buyer_name: "", transaction_code: "", line_amount: "" });

function saveDeclaration(id: number, declaration: Declaration, confirmed: boolean) {
  return api<CustomsDocument>(`/api/customs-documents/${id}`, {
    method: "PATCH", body: JSON.stringify({ declaration, confirmed })
  });
}

export function CustomsPage({ taxpayer, canPrepare }: {
  taxpayer: string; canPrepare: boolean;
}) {
  const [documents, setDocuments] = useState<CustomsDocument[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [uploadErrors, setUploadErrors] = useState({ggb: "", gcb: ""});
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [editor, setEditor] = useState<CustomsDocument | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [validationResult, setValidationResult] = useState<{
    confirmed: number; failures: { document: CustomsDocument; reason: string }[];
  } | null>(null);
  const [sources, setSources] = useState<CompanySource[]>([]);
  const uploadActive = useRef(false);

  async function load() {
    if (!taxpayer) { setDocuments([]); return; }
    const [rows, shared] = await Promise.all([
      api<CustomsDocument[]>(`/api/customs-documents?taxpayer=${taxpayer}`),
      api<CompanySource[]>(`/api/taxpayers/${taxpayer}/sources`)
    ]);
    setDocuments(rows); setSources(shared);
  }
  useEffect(() => { void load().catch(reason => setError(String(reason))); }, []);
  const visible = documents.filter(doc => String(doc.taxpayer) === taxpayer);
  const pendingDocuments = visible.filter(doc => !doc.confirmed);
  const selectedDocuments = pendingDocuments.filter(doc => selectedIds.includes(doc.id));

  async function validateSelected() {
    if (busy || !canPrepare || !selectedDocuments.length) return;
    if (!window.confirm(`Seçili ${selectedDocuments.length} beyannamenin alanlarını ve eşya kalemlerini kaynak belgelerle karşılaştırdıysanız toplu doğrulamayı başlatın.`)) return;
    setBusy(true);
    setValidationResult(null);
    const confirmed = new Map<number, CustomsDocument>();
    const failures: NonNullable<typeof validationResult>["failures"] = [];
    try {
      for (const doc of selectedDocuments) {
        setProgress(`${confirmed.size + failures.length + 1}/${selectedDocuments.length} beyanname doğrulanıyor…`);
        try {
          confirmed.set(doc.id, await saveDeclaration(doc.id, doc.declaration, true));
        } catch (reason) {
          failures.push({ document: doc, reason: reason instanceof Error ? reason.message : String(reason) });
        }
      }
      setDocuments(current => current.map(doc => confirmed.get(doc.id) ?? doc));
      setSelectedIds(failures.map(({ document }) => document.id));
      setValidationResult({ confirmed: confirmed.size, failures });
    } finally {
      setBusy(false);
      setProgress("");
    }
  }

  async function upload(files: FileList | null, kind: "ggb" | "gcb") {
    if (!files || uploadActive.current) return;
    setUploadErrors(current => ({...current, [kind]: ""}));
    if (!taxpayer) { setUploadErrors(current => ({...current, [kind]: "Önce şirket seçin."})); return; }
    const selected = Array.from(files).filter(file => file.name.toLowerCase().endsWith(".xlsx"));
    if (!selected.length) { setUploadErrors(current => ({...current, [kind]: `Dosya kabul edilmedi. ${kind === "ggb" ? "3.GGB_TEMPLATE" : "4.GCB_TEMPLATE"} şablonuna uygun .xlsx dosyası seçin.`})); return; }
    uploadActive.current = true;
    setBusy(true); setError(""); setMessage(""); setResults([]);
    const collected: UploadResult[] = [];
    try {
      for (let i = 0; i < selected.length; i++) {
        setProgress(`${i + 1}/${selected.length}: ${selected[i].name} okunuyor…`);
        const form = new FormData();
        form.append("kind", kind); form.append("taxpayer", taxpayer); form.append("files", selected[i]);
        try {
          const result = await api<{ results: UploadResult[] }>("/api/customs-documents", { method: "POST", body: form });
          collected.push(...result.results);
        } catch (reason) {
          collected.push({ filename: selected[i].name, status: "failed", error: reason instanceof Error ? reason.message : String(reason) });
        }
        setResults([...collected]);
        const failed = collected.find(result => result.status === "failed" || result.error);
        setUploadErrors(current => ({...current, [kind]: failed ? `${failed.filename}: ${failed.error || "Dosya işlenemedi."}` : ""}));
      }
      await load();
    } catch (reason) { setUploadErrors(current => ({...current, [kind]: reason instanceof Error ? reason.message : String(reason)})); }
    finally { uploadActive.current = false; setBusy(false); setProgress(""); }
  }

  return <>
    {error && <div className="notice warning" role="alert">{error}</div>}
    {message && <div className="notice">{message}</div>}
    <div className="customs-upload-grid">
      {sourceKinds.map(source => <CompanySourceUpload key={source.kind} taxpayer={taxpayer} source={source}
        canPrepare={canPrepare} onUploaded={load} />)}
      {(["ggb", "gcb"] as const).map(kind => <article className="panel" key={kind}>
        <div className="panel-head"><div><p className="eyebrow">{kind === "ggb" ? "İTHALAT" : "İHRACAT"}</p><h2>{kind === "ggb" ? "GGB yükle" : "GÇB / ETGB yükle"}</h2></div></div>
        <p>{kind === "ggb" ? "3.GGB_TEMPLATE.xlsx şablonunu doldurup yükleyin."
          : "4.GCB_TEMPLATE Excel’i, ihracat faturaları ve GTİP kalemleriyle eşleşir. İade dönemi kapanma tarihine göre belirlenir."}</p>
        {canPrepare && <div className="customs-upload-actions">
          <label className="secondary-button">Dosya Seç<input aria-label={`${kind === "ggb" ? "GGB" : "GÇB / ETGB"} Excel dosyaları`} type="file" accept=".xlsx" multiple disabled={busy || !taxpayer}
            onChange={event => { void upload(event.target.files, kind); event.target.value = ""; }} /></label>
        </div>}
        {uploadErrors[kind] && <div className="notice source-upload-error" role="alert"><strong>Yükleme tamamlanamadı.</strong> {uploadErrors[kind]}</div>}
      </article>)}
    </div>
    {progress && <div className="notice" role="status">{progress}</div>}
    {!!results.length && <article className="panel customs-panel"><h2>Yükleme sonucu</h2>{results.map((result, index) =>
      <div className="customs-result" key={index}><strong>{result.filename}</strong><span>{result.error || (result.status === "duplicate" ? "Zaten yüklü" : "Yüklendi, alan kontrolü bekliyor")}</span></div>)}</article>}
    {sourceKinds.map(source => {
      const rows = sources.filter(row => row.kind === source.kind);
      return <details className="panel source-document-list" key={source.kind}>
        <summary><strong>{source.title.replace(" yükle", "")}</strong><span>{rows.length} kayıt</span></summary>
        <div className="table-list">{rows.length ? rows.map(row => <div className="source-document-row" key={row.id}>
          <span><strong>{row.filename}</strong><small>{row.period_start || "Dönem okunamadı"}{row.period_end && row.period_end !== row.period_start ? ` — ${row.period_end}` : ""}</small></span>
          <span className={`pill ${row.status}`}>{sourceStatus[row.status] || row.status}</span>
          {!!row.issues.length && <small>{row.issues.map(issue => typeof issue === "string" ? issue : issue.message).join(" · ")}</small>}
        </div>) : <p>Henüz belge yüklenmedi.</p>}</div>
      </details>;
    })}
    <details className="panel source-document-list"><summary><strong>GGB / GÇB / ETGB beyannameleri</strong><span>{visible.length} kayıt</span></summary>
      {!visible.length && <p>Yüklenen beyannameler burada listelenecek.</p>}
      {canPrepare && pendingDocuments.length > 0 && <div className="customs-upload-actions">
        <button className="secondary-button" disabled={busy || selectedDocuments.length === pendingDocuments.length}
          onClick={() => setSelectedIds(pendingDocuments.map(doc => doc.id))}>
          Doğrulama bekleyenlerin hepsini seç
        </button>
        <button className="primary-button" disabled={busy || !selectedDocuments.length} onClick={() => void validateSelected()}>
          Toplu doğrula ({selectedDocuments.length})
        </button>
      </div>}
      {validationResult && <div className="notice" role="status">
        <p>{validationResult.confirmed} kayıt doğrulandı. {validationResult.failures.length} kayıt doğrulanamadı.</p>
        {!!validationResult.failures.length && <ul>{validationResult.failures.map(({ document, reason }) =>
          <li key={document.id}><strong>{document.declaration.registration_no || document.filename}</strong> — {reason}</li>
        )}</ul>}
      </div>}
      <div className="table-list">{visible.map(doc => <div className="customs-document-row" key={doc.id}>
        <span><strong>{canPrepare && !doc.confirmed && <input type="checkbox" disabled={busy}
          aria-label={`${doc.declaration.registration_no || doc.filename} beyannamesini seç`}
          style={{ width: 16, height: 16, minHeight: 16, verticalAlign: "middle" }}
          checked={selectedIds.includes(doc.id)}
          onChange={event => {
            const checked = event.target.checked;
            setSelectedIds(current => checked ? [...current, doc.id] : current.filter(id => id !== doc.id));
          }} />} {doc.kind === "ggb" ? "GGB" : doc.declaration.declaration_type || "GÇB"} · {doc.declaration.registration_no}</strong><small>{doc.taxpayer_vkn} · {doc.taxpayer_title}</small><small>{doc.filename}</small></span>
        <span><strong>{doc.declaration.invoice_amount || "Tutar eksik"} {doc.declaration.currency}</strong><small>{doc.kind === "ggb" ? `Tescil: ${doc.declaration.registration_date || "Eksik"}` : `Kapanma: ${doc.declaration.closure_date || "Eksik"}`}</small>
          <small>{doc.cases.map(c => `${c.title} (${c.transaction_code})`).join(", ") || "Dönem/kod eşleşen dosya yok"}</small></span>
        <span className={`pill ${doc.confirmed ? "active" : "partial"}`}>{doc.confirmed ? "Doğrulandı" : "Kontrol bekliyor"}</span>
        <button className="text-button" disabled={busy} onClick={() => setEditor(doc)}>{canPrepare ? "Kontrol et / düzenle" : "İncele"}</button>
      </div>)}</div>
    </details>
    {editor && <DeclarationEditor document={editor} canPrepare={canPrepare} onClose={() => setEditor(null)} onSaved={doc => {
      setDocuments(current => current.map(d => d.id === doc.id ? doc : d)); setEditor(null);
    }} />}
  </>;
}


type CompanySource = {
  id: number; kind: string; filename: string; period_start?: string; period_end?: string;
  status: string; row_count: number; issues: (string | {message: string})[];
};
const sourceStatus: Record<string, string> = {succeeded: "Hazır", partial: "Kontrol gerekli", failed: "Hata", conflict: "Çelişen kaynak", processing: "İşleniyor", superseded: "Yerine yenisi yüklendi"};
const sourceKinds = [
  {kind: "eledger", title: "e-Defter yükle", eyebrow: "MUHASEBE", accept: ".xml", detail: "Şirketin e-Defter XML dosyalarını yükleyin."},
  {kind: "general_ledger", title: "Muavin yükle", eyebrow: "MUHASEBE", accept: ".xlsx,.xls,.csv", detail: "Şirketin muavin kayıtlarını yükleyin."},
  {kind: "kdv1", title: "Beyanname yükle", eyebrow: "KDV1", accept: ".pdf,.xml,.json,.xlsx,.csv", detail: "KDV1 beyannamelerini yükleyin. Dönemler belgeden okunur."},
  {kind: "capacity_report", title: "Kapasite raporu yükle", eyebrow: "ÜRETİM", accept: ".xlsx", detail: "6.KAPASITE_RAPORU_TEMPLATE.xlsx şablonunu yükleyin."}
];
function CompanySourceUpload({taxpayer, source, canPrepare, onUploaded}: {
  taxpayer: string; source: typeof sourceKinds[number]; canPrepare: boolean; onUploaded: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  async function upload(files: File[]) {
    if (!taxpayer || busy || !files.length) return;
    setUploadError("");
    const extensions = source.accept.split(",");
    files = files.filter(file => extensions.some(extension => file.name.toLowerCase().endsWith(extension)));
    if (!files.length) { setMessage(""); setUploadError(`Dosya kabul edilmedi. Desteklenen dosya türleri: ${source.accept}.`); return; }
    setBusy(true); setMessage("");
    let ready = 0, partial = 0, failed = 0, skipped = 0;
    let error = "";
    try {
      if (source.kind === "eledger") {
        setMessage("e-Defter dosyaları kontrol ediliyor…");
        const ledgerFiles: File[] = [];
        for (const file of files) {
          if (await isLedgerXml(file)) ledgerFiles.push(file);
          else skipped++;
        }
        files = ledgerFiles;
      }
      for (let i = 0; i < files.length; i++) {
        setMessage(`${i + 1}/${files.length}: ${files[i].name} işleniyor…`);
        const body = new FormData(); body.append("kind", source.kind); body.append("file", files[i]);
        try {
          const result = await api<{status: string; issues?: {message: string}[]}>(`/api/taxpayers/${taxpayer}/sources`, {method: "POST", body});
          if (result.status === "succeeded") ready++;
          else if (result.status === "partial") partial++;
          else {
            failed++;
            error ||= `${files[i].name}: ${result.issues?.map(issue => issue.message).join("; ") || "Dosya işlenemedi."}`;
          }
        } catch (reason) { failed++; error ||= `${files[i].name}: ${reason instanceof Error ? reason.message : String(reason)}`; }
      }
      await onUploaded();
    } catch (reason) { error = reason instanceof Error ? reason.message : String(reason); }
    finally {
      setBusy(false);
      setUploadError(error);
      setMessage([
        `${ready} dosya hazır`,
        partial ? `${partial} kontrol gerekli` : "",
        failed ? `${failed} hata` : "",
        skipped ? `${skipped} berat / yardımcı XML atlandı` : "",
      ].filter(Boolean).join(" · "));
    }
  }
  return <article className="panel"><div className="panel-head"><div><p className="eyebrow">{source.eyebrow}</p><h2>{source.title}</h2></div></div>
    <p>{source.detail}</p>
    {canPrepare && <div className="customs-upload-actions"><label className="secondary-button">Dosya seç<input type="file" multiple accept={source.accept} disabled={busy || !taxpayer}
      onChange={event => {const files = Array.from(event.target.files || []); event.target.value = ""; void upload(files);}} /></label>
      {source.kind !== "capacity_report" && <label className="secondary-button">Klasör seç<input type="file" multiple aria-label={`${source.title} klasörü`}
        ref={input => { input?.setAttribute("webkitdirectory", ""); input?.setAttribute("directory", ""); }}
        disabled={busy || !taxpayer}
        onChange={event => {const files = Array.from(event.target.files || []); event.target.value = ""; void upload(files);}} /></label>}
    </div>}
    {message && <p className="source-upload-message" role="status">{message}</p>}
    {uploadError && <div className="notice source-upload-error" role="alert"><strong>Yükleme tamamlanamadı.</strong> {uploadError}</div>}
  </article>;
}

function DeclarationEditor({ document: doc, canPrepare, onClose, onSaved }: {
  document: CustomsDocument; canPrepare: boolean; onClose: () => void; onSaved: (doc: CustomsDocument) => void;
}) {
  const [data, setData] = useState<Declaration>(structuredClone(doc.declaration));
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  function field(key: keyof Omit<Declaration, "items">, label: string, type = "text") {
    return <label>{label}<input type={type} value={data[key] || ""} disabled={!canPrepare || busy || key === "vkn"}
      onChange={e => setData(current => ({ ...current, [key]: e.target.value }))} /></label>;
  }
  function itemField(index: number, key: keyof Item, label: string, type = "text") {
    return <input aria-label={`Kalem ${index + 1} ${label}`} type={type} value={data.items[index][key] || ""} disabled={!canPrepare || busy}
      onChange={e => setData(current => ({ ...current, items: current.items.map((item, i) => i === index ? { ...item, [key]: e.target.value } : item) }))} />;
  }
  async function save(confirmed: boolean) {
    setBusy(true); setError("");
    try { onSaved(await saveDeclaration(doc.id, data, confirmed)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  }
  return <div className="modal-backdrop" role="presentation"><section className="customs-editor panel" role="dialog" aria-modal="true" aria-label="Beyanname kontrolü">
    <div className="panel-head"><div><h2>{doc.kind === "ggb" ? "GGB" : doc.declaration.declaration_type || "GÇB"} belge kontrolü</h2><small>{doc.filename} · <a href={doc.source_url} target="_blank" rel="noopener noreferrer">Kaynak Excel’i indir</a></small></div><button className="text-button" autoFocus disabled={busy} onClick={onClose}>Kapat</button></div>
    {error && <div className="notice warning" role="alert">{error}</div>}
    <div className="customs-editor-grid"><a className="secondary-button" href={doc.source_url}>Kaynak Excel’i indir</a>
      <div className="customs-fields"><div className="notice warning">{doc.warnings.map((warning, i) => <p key={i}>{warning}</p>)}</div>
        {field("vkn", "Beyanname sahibi VKN")}{field("registration_no", "Tescil numarası")}
        {doc.kind === "gcb" && <label>Beyanname türü<select value={data.declaration_type || "GÇB"} disabled={!canPrepare || busy}
          onChange={e => setData(current => ({ ...current, declaration_type: e.target.value as "GÇB" | "ETGB" }))}>
          <option value="GÇB">GÇB</option><option value="ETGB">ETGB</option>
        </select></label>}
        {field("registration_date", "Tescil tarihi", "date")}
        {field("issuing_authority", "Tescil gümrük müdürlüğü")}
        {doc.kind === "gcb" && field("closure_date", "Kapanma tarihi (gümrük/VEDOP)", "date")}
        {field("currency", "Döviz cinsi")}{field("invoice_amount", "Toplam fatura bedeli (döviz)")}
        {doc.kind === "ggb" && field("import_vat", "Hesaplanan ithalat KDV’si (TL)")}
      </div></div>
    {doc.kind === "gcb" && <><h3>İhraç edilen eşya ve fatura kalemleri</h3><p>Her fatura / GTİP / birim / işlem türü için bir satır. Adet için C62, kilogram için KGM, metrekare için MTK kullanın.</p>
      <div className="customs-items-scroll"><table className="customs-items"><thead><tr>{["GTİP", "Miktar", "Birim", "Fatura tarihi", "Seri", "Fatura no", "Alıcı", "İşlem türü", "Kalem tutarı (döviz)", ""].map((label, i) => <th key={i}>{label}</th>)}</tr></thead>
        <tbody>{data.items.map((item, index) => <tr key={index}>
          <td>{itemField(index, "gtip", "GTİP")}</td><td>{itemField(index, "quantity", "miktar")}</td><td>{itemField(index, "unit_code", "birim")}</td>
          <td>{itemField(index, "invoice_date", "fatura tarihi", "date")}</td><td>{itemField(index, "invoice_series", "seri")}</td>
          <td>{itemField(index, "invoice_no", "fatura no")}</td><td>{itemField(index, "buyer_name", "alıcı")}</td>
          <td><select aria-label={`Kalem ${index + 1} işlem türü`} value={item.transaction_code} disabled={!canPrepare || busy}
            onChange={e => setData(current => ({ ...current, items: current.items.map((row, i) => i === index ? { ...row, transaction_code: e.target.value } : row) }))}>
            <option value="">Seçin</option><option value="301">301</option><option value="338">338</option></select></td>
          <td>{itemField(index, "line_amount", "döviz tutarı")}</td><td>{canPrepare && <button className="text-button danger" disabled={busy} onClick={() => setData(current => ({ ...current, items: current.items.filter((_, i) => i !== index) }))}>Sil</button>}</td>
        </tr>)}</tbody></table></div>
      {canPrepare && <button className="secondary-button" disabled={busy} onClick={() => setData(current => ({ ...current, items: [...current.items, blankItem()] }))}>Kalem ekle</button>}
    </>}
    {canPrepare && <div className="customs-editor-footer"><label><input type="checkbox" checked={checked} disabled={busy} onChange={e => setChecked(e.target.checked)} /> Beyanname alanlarını ve bütün eşya kalemlerini kaynak belgeyle karşılaştırdım.</label>
      <div className="customs-upload-actions"><button className="secondary-button" disabled={busy} onClick={() => void save(false)}>Taslak kaydet</button>
        <button className="primary-button" disabled={busy || !checked} onClick={() => void save(true)}>Doğrula ve kaydet</button></div></div>}
  </section></div>;
}
